import ArrowDownTrayIcon from "@heroicons/react/24/solid/ArrowDownTrayIcon"
import ActionButtonComponent from "../../../ui-components/ActionButtonComponent";
import {downloadBlob} from "@feature/commons/lib/utils";
import { PDFDocument } from "pdf-lib";

export const buildServicesSurveysDocuments = (
	worksite,
	servicesSurveys,
	worksiteUsers,
	setChosenSurveys,
	setShowReportsSelectionWorksiteDialog,
	setReportDialogCallback,
	downloadDesignSurveysPdf,
	intl,
	availableDesignSurveys,
	userData
) => {

	const buildSurveyInfo = async (survey) => {

		const isoStrM = survey['modified_at'] || survey['created_at'];
		const dateM = new Date(isoStrM);
		const offset = dateM.getTimezoneOffset() * 60 * 1000;
		const timestampWithOffsetM = dateM.getTime();
		const timestampWithoutOffsetM = timestampWithOffsetM - offset;
		const dateWithoutOffsetM = new Date(timestampWithoutOffsetM);

		const modifier = (survey.id_modifier || survey.id_creator)
			&& worksiteUsers?.find((u) => u.id === survey.id_modifier)?.name;
		return !!modifier ?
			intl.formatMessage(
				{id: "last_modified_at_by"},
				{
					date: intl.formatDate(new Date(dateWithoutOffsetM), {
						day: "numeric",
						month: "long",
						year: "numeric"
					}),
					time: intl.formatTime(new Date(dateWithoutOffsetM)),
					by: modifier,
				}
			)
			: "";
	}

	const openReportDialog = (selectedSurveys, onReportDownload=undefined) => {
		setChosenSurveys(selectedSurveys);
		setReportDialogCallback(!!onReportDownload ? () => onReportDownload : undefined);
		setShowReportsSelectionWorksiteDialog(true);
	}

	const getSectionKey = (service) => {
		if (service.class === 0) return "designs";
		if (service.class === 1) return "configurations";
		if (service.class === 2) return "services";
		throw new Error(`Unknown service class ${service.class}`);
	}

	const mergeAndDownload = async (surveyIds, mainBlob) => {
		const blobs = [mainBlob, ...await downloadDesignSurveysPdf(surveyIds)];
		// create a new PDF document (empty) and copy all pages from the blobs
		const mergedPdf = await PDFDocument.create();
		for (const blob of blobs) {
			const arrayBuffer = await blob.arrayBuffer();
			const pdf = await PDFDocument.load(arrayBuffer);
			const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
			copiedPages.forEach((page) => mergedPdf.addPage(page));
		}
		// download the merged PDF
		downloadBlob(
			new Blob([await mergedPdf.save()], {type: "application/pdf"}),
			!!worksite?.des_worksite
				? `REPORT ${worksite.des_worksite.toUpperCase()}.pdf`
				: "report.pdf"
		)
	}

	const hasServicePermission = (service) => {
		const rPerm = service?.r_perm ?? [];
		return (
			rPerm.includes("public")
			|| rPerm.includes(userData?.role_type)
			|| rPerm.includes(userData?.account_user_role_type)
			|| (userData?.groups ?? []).some((g) => rPerm.includes("group_" + g))
		);
	}

	const groupSurveysByService = (surveys) => {
		/*
		grouping surveys by service, making a group for each different service,
		the group will contain an array of documents.
		 */
		return surveys.filter((survey) => hasServicePermission(survey.services)).reduce((acc, survey) => {
			if (!acc[survey.service_id]) {
				acc[survey.service_id] = {
					title: `${survey.services.name} (${surveys.filter(s => s.service_id === survey.service_id).length})`,
					class: survey.services.class,
					documents: []
				}
			}
			/*
			each document has:
			- a name -> the survey name
			- the survey_id -> in order to be able to refer to the survey
			- info -> created at ... by ...
			- what to do when the document is clicked
			- selected -> whether the document is selected (checkbox clicked)
			- visible -> whether the document is visible (matches the search filter)
			 */
			acc[survey.service_id].documents.push({
				survey_id: survey.id,
				name: survey.name || intl.formatMessage({id: "unnamed_configuration"}),
				info: buildSurveyInfo(survey),
				onDocumentClicked: async () => {
					if (getSectionKey(survey.services) === 'configurations') {
						openReportDialog([survey.id])
					} else {
						openReportDialog([survey.id],async (mainBlob) => {
							await mergeAndDownload([survey.id], mainBlob);
						});
					}
				},
				selected: false,
				visible: true
			});
			return acc;
		}, {});
	}

	const groupSurveysByClass = (surveys) => {
		const groupedByService = !!surveys
			? groupSurveysByService(surveys)
			: undefined
		const classMapping = {
			0: {key: "designs", alternateColor: "bg-teal bg-opacity-60"},
			1: {key: "configurations", alternateColor: "bg-kkgreen-400 bg-opacity-60"},
			2: {key: "services", alternateColor: "bg-ivory-100"},
		}
		return [0, 1, 2].reduce(
			(acc, targetClass) => {
				const {key, alternateColor} = classMapping[targetClass];
				return {
					...acc,
					[key]: {
						title: intl.formatMessage({id: key}),
						alternateColor,
						groups: !!groupedByService
							? Object.entries(groupedByService)
								.filter(([_, {class: currentCls}]) => currentCls === targetClass)
								.map(([_, data]) => data)
							: undefined,
						actions:{
							download: {
								// MAKE ALL SURVEYS SELECTED CHOSEN FOR THE REPORT GENERATION
								actionComponent: (documentsSelected, visible) => <ActionButtonComponent
									icon={ArrowDownTrayIcon}
									action={() => {
										const surveyIds = documentsSelected.map(d => d.survey_id)
										if (key === 'configurations') {
											openReportDialog(surveyIds)
										} else if (key === 'designs') {
											const hasDesignSurveys = surveyIds.some(
												(sid) => availableDesignSurveys?.some((s) => s.id === sid)
											);
											let mergeReportsFn = undefined;
											if (hasDesignSurveys)
												mergeReportsFn = async (mainBlob) => {
													await mergeAndDownload(surveyIds, mainBlob);
												};
											openReportDialog(surveyIds, mergeReportsFn);
										} else {  // services
											throw new Error("Services surveys are not available for download");  // TODO implement this
										}
									}}
								/>
							}
						},
						// onAllActions: [{
						// 	// MAKE AL SURVEYS CHOSEN FOR THE REPORT GENERATION
						// 	actionComponent: (documents, disabled) => <ActionButtonComponent
						// 		icon={ArrowDownTrayIcon}
						// 		action={async () => {
						// 			if (key === 'configurations') {
						// 				const surveyIds = documents.map(d => d.survey_id)
						// 				openReportDialog(surveyIds)
						// 			} else {
						// 				await downloadDesignSurveys(documents);
						// 			}
						// 		}}
						// 		disabled={disabled}
						// 	/>
						// }],
						// onSelectedActions: [{
						// 	// MAKE ALL SURVEYS SELECTED CHOSEN FOR THE REPORT GENERATION
						// 	actionComponent: (documentsSelected) => <ActionButtonComponent
						// 		icon={ArrowDownTrayIcon}
						// 		action={async () => {
						// 			if (key === 'configurations') {
						// 				const surveyIds = documentsSelected.map(d => d.survey_id)
						// 				openReportDialog(surveyIds)
						// 			} else {
						// 				await downloadDesignSurveys(documentsSelected);
						// 			}
						// 		}}
						// 		styleType={"white"}
						// 		text={intl.formatMessage({id: 'download'})}
						// 	/>
						// }],
					},
				};
			},
			{},
		);
	}

	return groupSurveysByClass(servicesSurveys)
}